<template>
  <div class="player-idle-phase">
    <IdleChromeShell :compact-layout="false" :hibernate="false">
      <div
        class="flex flex-col items-center text-center gap-[1.5vh]"
        style="max-width: min(80vw, 90vh);"
        role="status"
        aria-busy="true"
        :aria-label="statusLabel"
      >
        <PlayerIdleStatusLine>{{ statusLabel }}</PlayerIdleStatusLine>
        <div
          v-if="showProgressBar"
          class="flex flex-col items-center gap-[1.2vh] text-center"
          style="width: clamp(8rem, 25vw, 22rem); max-width: min(80vw, 90vh);"
        >
          <span
            v-if="downloadPercentage != null"
            class="text-white/40 font-light tracking-widest uppercase"
            style="font-size: clamp(0.55rem, 0.85vw, 0.8rem);"
          >
            Baixando mídias &nbsp;{{ downloadPercentage }}%
          </span>
          <div class="w-full h-[2px] rounded-full bg-white/10 overflow-hidden">
            <div
              v-if="indeterminateProgress"
              class="idle-loading-progress-indeterminate h-full rounded-full bg-white/50"
            />
            <div
              v-else-if="downloadPercentage != null"
              class="h-full rounded-full bg-white/50 transition-all duration-300 ease-out"
              :style="{ width: `${downloadPercentage}%` }"
            />
          </div>
        </div>
      </div>
    </IdleChromeShell>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue'
import IdleChromeShell from '@/components/IdleChromeShell.vue'
import PlayerIdleStatusLine from '@/components/PlayerIdleStatusLine.vue'
import usePlayer from '@/composables/usePlayer'

const { state } = usePlayer()
const { loadingCampaign, downloadProgress, postPlaylistLoadHoldActive } = toRefs(state)

const downloadPercentage = computed(() => {
  if (postPlaylistLoadHoldActive.value) {
    return 100
  }
  if (!loadingCampaign.value || Number.isNaN(downloadProgress.value)) {
    return null
  }
  if (downloadProgress.value <= 0) {
    return null
  }
  return Math.min(100, Math.max(1, Math.round(downloadProgress.value * 100)))
})

const showProgressBar = computed(() =>
  postPlaylistLoadHoldActive.value
  || (loadingCampaign.value && !Number.isNaN(downloadProgress.value) && downloadProgress.value < 1)
)

const indeterminateProgress = computed(() =>
  showProgressBar.value
  && !postPlaylistLoadHoldActive.value
  && downloadPercentage.value == null
)

const statusLabel = computed(() => {
  if (showProgressBar.value && downloadPercentage.value != null) {
    return 'A carregar mídias'
  }
  if (showProgressBar.value) {
    return 'A carregar mídias…'
  }
  return 'A preparar mídias'
})
</script>

<style scoped>
@keyframes idle-loading-progress-indeterminate {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}

.idle-loading-progress-indeterminate {
  width: 35%;
  animation: idle-loading-progress-indeterminate 1.35s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .idle-loading-progress-indeterminate {
    width: 100%;
    opacity: 0.45;
    animation: none;
  }
}
</style>
